import { apiClient } from './apiClient';
import type { TaskStatus, TaskPriority } from '@/types';

// =====================================================
// Types khớp 1-1 với backend DTOs
//   UserDashboardDto / DepartmentDashboardDto / ExecutiveDashboardDto
//   (HVTaskFlow.Application/Common/Dtos.cs)
// Backend serialize camelCase (JsonNamingPolicy.CamelCase).
// DateOnly → chuỗi "yyyy-MM-dd"; DateTimeOffset → chuỗi ISO-8601.
// =====================================================

// ---------- Dashboard cá nhân ----------

export interface ChartPoint {
  /** yyyy-MM-dd */
  date: string;
  count: number;
}

export interface SimpleTask {
  id: string;
  code: string;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  /** yyyy-MM-dd */
  dueDate: string;
}

export interface PendingSignature {
  id: string;
  documentName: string;
  createdAt: string;
  dueDate: string | null;
  requesterName: string | null;
  /** V1.0.60: nội dung mô tả/yêu cầu ký từ người gửi */
  taskDescription?: string | null;
}

export interface UserDashboard {
  inProgress: number;
  dueSoon: number;
  overdue: number;
  completedThisWeek: number;
  pendingSignatures: number;
  chart7Days: ChartPoint[];
  todayTasks: SimpleTask[];
  pendingSigList: PendingSignature[];
}

// ---------- Dashboard khoa/phòng ----------

export interface CategoryDistribution {
  category: string;
  count: number;
}

export interface StaffWorkload {
  userId: string;
  fullName: string;
  totalOpen: number;
  onTrack: number;
  warning: number;
  late: number;
}

export interface OverdueTask {
  id: string;
  code: string;
  title: string;
  assigneeName: string | null;
  /** yyyy-MM-dd */
  dueDate: string;
  daysOverdue: number;
  /** V1.0.34 H3: TRUE = khoa là chủ quản task; FALSE = khoa đang hỗ trợ */
  isPrimary?: boolean;
}

export interface DepartmentDashboard {
  departmentId: string;
  departmentCode: string;
  departmentName: string;
  staffCount: number;
  openTasks: number;
  completedTasks: number;
  overdueTasks: number;
  dueSoonTasks: number;
  distribution: CategoryDistribution[];
  workload: StaffWorkload[];
  overdueList: OverdueTask[];
  /** V1.0.34 H3: số task khoa là chủ quản */
  primaryTasks?: number;
  /** V1.0.34 H3: số task khoa hỗ trợ */
  supportingTasks?: number;
}

// ---------- Dashboard ban giám đốc ----------

export interface DepartmentSummary {
  id: string;
  code: string;
  name: string;
  staffCount: number;
  openTasks: number;
  completedTasks: number;
  overdueTasks: number;
  completionRate: number;
}

export interface ExecutiveTrendPoint {
  /** yyyy-MM-dd */
  date: string;
  created: number;
  completed: number;
}

export interface ExecutiveDashboard {
  openTasksTotal: number;
  completedTasksTotal: number;
  overdueTasksTotal: number;
  completedThisMonth: number;
  totalUsers: number;
  totalDepartments: number;
  overallCompletionRate: number;
  departmentStats: DepartmentSummary[];
  trend30Days: ExecutiveTrendPoint[];
}

// =====================================================
// Service
// =====================================================

export const dashboardService = {
  /** Dashboard của chính user đang đăng nhập. */
  async user(): Promise<UserDashboard> {
    const { data } = await apiClient.get<UserDashboard>('/dashboard/user');
    return data;
  },

  /**
   * Dashboard theo khoa/phòng.
   * Bỏ trống departmentId → backend tự lấy khoa của user hiện tại.
   */
  async department(departmentId?: string | null): Promise<DepartmentDashboard> {
    const { data } = await apiClient.get<DepartmentDashboard>('/dashboard/department', {
      params: departmentId ? { departmentId } : undefined,
    });
    return data;
  },

  /** Dashboard tổng quan toàn viện (SYS_ADMIN / BOD). */
  async executive(): Promise<ExecutiveDashboard> {
    const { data } = await apiClient.get<ExecutiveDashboard>('/dashboard/executive');
    return data;
  },
};
